import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { media } from '@atlaskit/primitives';
import UFOSegment from '@atlaskit/react-ufo/segment';

import { ContentBlock } from '@/components/wpl/content-block/src/content-block';
import { wacToken } from '@/libraries/design/foundations';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';

import { useLocalizedImage } from '../use-localized-image';

const heroContainerStyles = css({
	padding: '90px 0 64px',
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'center',
	alignItems: 'center',
	minHeight: 0,
	maxWidth: 'fit-content',
	margin: '0 auto',
	gap: '32px',
	[media.below.md]: {
		flexDirection: 'column',
		padding: '50px 0 30px',
	},
	// Tech debt: Adding this disable to unblock commits for GWP-2646
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.textContainerStyles': {
		[media.below.md]: {
			// Tech debt: Adding this disable to unblock commits for GWP-2646
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			'.contentBlock-linkFlex': {
				marginBlockEnd: '0',
			},
		},
		// Tech debt: Adding this disable to unblock commits for GWP-2646
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		h1: {
			color: wacToken('color.primary.black'),
			fontSize: '3rem',
			width: '466px',
			fontWeight: 800,
			lineHeight: '48px',
			WebkitFontSmoothing: 'antialiased',
			margin: '0 0 8px 0 !important',
			[media.only.sm]: {
				width: '648px',
				fontSize: '3.75rem',
				lineHeight: '4.375rem',
			},
			[media.below.sm]: {
				width: '312px',
				fontSize: '2.5rem',
				lineHeight: '2.875rem',
			},
		},
		// Tech debt: Adding this disable to unblock commits for GWP-2646

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		p: {
			color: wacToken('color.primary.black'),
			width: '466px',
			fontWeight: 400,
			fontSize: '1rem',
			lineHeight: '24px',
			WebkitFontSmoothin: 'antialiased',
			margin: '0 0 16px 0',
			[media.only.sm]: {
				width: '648px',
			},
			[media.below.sm]: {
				width: '312px',
			},
		},
		// Tech debt: Adding this disable to unblock commits for GWP-2646
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		a: {
			margin: '0',
		},
		// Tech debt: Adding this disable to unblock commits for GWP-2646
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'.text-section': {
			padding: '0 0 0 18px',
			// Tech debt: Adding this disable to unblock commits for GWP-2646
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			'& > *': {
				margin: '0 0 16px 0',
			},
		},
	},
});

const imageContainerStyles = css({
	position: 'relative',
});

const imageStyles = css({
	backgroundRepeat: 'no-repeat',
	backgroundSize: 'cover',
	textAlign: 'center',
	width: '668px',
	maxWidth: '100%',
	[media.below.md]: {
		width: '648px',
	},
	[media.below.sm]: {
		width: '311px',
	},
});

export const HeroSectionImage = () => {
	return (
		<div css={imageContainerStyles}>
			{/* eslint-disable-next-line no-restricted-syntax */}
			<img alt="" css={imageStyles} src={useLocalizedImage('heroImage')} loading="lazy" />
		</div>
	);
};

export const HeroSection = () => {
	const intl = useIntl();
	const showGwp7999JiraFeaturesUpdates = checkFeatureFlagGate(featureFlags.gwp7999JiraFeatures);

	const heroHeaderText = showGwp7999JiraFeaturesUpdates
		? intl.formatMessage({
				id: 'JiraSoftwareFeaturesPageV2.Hero.Gwp7999Header',
				defaultMessage: "Get to know Jira's project management features",
			})
		: intl.formatMessage({
				id: 'JiraSoftwareFeaturesPageV2.Hero.Header',
				defaultMessage: 'Project management features for all teams',
			});

	const heroBodyText = showGwp7999JiraFeaturesUpdates
		? intl.formatMessage({
				id: 'JiraSoftwareFeaturesPageV2.Hero.Gwp7999Body',
				defaultMessage: 'Built to help every team go from big idea to launch faster.',
			})
		: intl.formatMessage({
				id: 'JiraSoftwareFeaturesPageV2.Hero.Body',
				defaultMessage:
					'Built for every team, Jira is how modern organizations take work from to-do to done.',
			});

	const buttonText = intl.formatMessage({
		id: 'JiraSoftwareFeaturesPageV2.Cta.TryJiraFree',
		defaultMessage: 'Try Jira free',
	});

	return (
		<UFOSegment name="hero">
			<div css={heroContainerStyles}>
				<ContentBlock
					headerText={heroHeaderText}
					headerType="h1"
					bodyText={heroBodyText}
					className="textContainerStyles"
					ctaProps={{
						buttonProps: {
							value: buttonText,
							href: useLocalizedHref('/software/jira/free'),
							variant: 'primary',
							color: 'blue',
							dangerouslySetInnerHTML: { __html: buttonText },
							dataEventProps: {
								'data-product-key': 'jiraSoftware',
								'data-event': 'clicked',
								'data-event-container': 'heroSection',
								'data-event-component': 'linkButton',
							},
						},
					}}
				/>
				<HeroSectionImage />
			</div>
		</UFOSegment>
	);
};
